import { useEffect, useRef, useState, type ReactNode } from "react";
import { CheckIcon, ChevronDownIcon, ClockIcon, XIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Frame, FrameAction, FrameCount, FrameDescription, FrameHeader, FramePanel, FrameTitle } from "@/components/ui/frame";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { RelativeTime } from "@/components/dashboard_sourcing/RelativeTime";
import { SeverityIcon } from "./parts";
import type { CampaignChange, OpsAction, OpsActionRecord, OpsCapabilities, OpsDecision, OpsSourcingRequest, Resolution } from "./types";

/**
 * The Success controller's queue on the board (amy/lib/ops — docs/
 * success-controller.md): the decisions the rules raised, each with its lever
 * and Accept / Snooze / Dismiss; the sourcing requests; the actions log; the
 * capabilities panel. The ONE write on the whole board is resolving a
 * decision (useOpsQueue.resolve): while amy runs with OPS_WRITE_ACTIONS off an
 * Accept is RECORDED as would-do and nothing is called — WritesBadge says
 * which state applies.
 */

const RULE_LABEL: Record<OpsDecision["rule"], string> = {
  goal_pace: "goal pace",
  runway: "runway",
  mailbox_health: "mailbox health",
  stuck_state: "stuck",
  sourcing: "sourcing",
  scoped_search: "scoped search",
  decline_review: "judgment trigger",
  autonomy: "autonomy",
  judgment: "judgment",
};

const money = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
const num = (n: number) => Math.round(n).toLocaleString("en-US");

/** Mirrors amy/lib/ops/campaignChange.ts describeCampaignChange. */
function describeChange(c: CampaignChange): string {
  const parts: string[] = [];
  if (c.flat_fee_usd) parts.push(`tier ${c.flat_fee_usd.tier} flat fee → ${money(c.flat_fee_usd.amount)}`);
  if (c.follower_range) parts.push(`followers ${num(c.follower_range[0])}–${c.follower_range[1] === 0 ? "∞" : num(c.follower_range[1])}`);
  if (c.median_views_range) parts.push(c.median_views_range[0] === 0 && c.median_views_range[1] === 0 ? "median-views filter cleared" : `median views ${num(c.median_views_range[0])}–${c.median_views_range[1] === 0 ? "∞" : num(c.median_views_range[1])}`);
  if (c.add_creators) parts.push(`search for ${c.add_creators} more creators`);
  return parts.join("; ") || "(no change)";
}

function actionLabel(action: OpsAction | null): string {
  if (!action) return "No lever — acknowledge";
  switch (action.type) {
    case "set_campaign_mailbox":
      return `Switch off ${action.mailbox}${action.requeue_unreplied ? " and re-queue its unanswered creators" : ""}`;
    case "dispatch_sourcing":
      return `Dispatch sourcing batch ${action.request_id}`;
    case "run_scoped_search":
      return `Run the campaign search scoped to ${action.batch_name}`;
    case "judgment_session":
      return `Start a ${action.topic.replace(/_/g, " ")} session (success subagent)`;
    case "campaign_change":
      return `Change the campaign: ${describeChange(action.change)}`;
    case "manual":
      return action.instruction;
  }
}

export function WritesBadge({ writes }: { writes: boolean | null }) {
  if (writes === null) return null;
  return (
    <Badge
      variant={writes ? "error" : "muted"}
      title={writes ? "OPS_WRITE_ACTIONS=on: accepted decisions and auto rules call the executors" : "OPS_WRITE_ACTIONS is off: every Accept is recorded as “would do”; nothing is called"}
    >
      {writes ? "writes ON" : "writes off — recording only"}
    </Badge>
  );
}

type ResolveFn = (decision: OpsDecision, resolution: Resolution, opts?: { snoozeDays?: number }) => Promise<string | null>;

/** Rule titles often start with the client's name, which the row already shows (older filed proposals carry it twice). */
function titleWithoutClient(title: string, clientName: string): string {
  const prefix = `${clientName}: `;
  let out = title;
  while (out.startsWith(prefix)) out = out.slice(prefix.length);
  return out;
}

function IconAction({ label, busy, onClick, children }: { label: string; busy: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button size="icon-sm" variant="muted-ghost" disabled={busy} onClick={onClick} aria-label={label}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * One decision: a compact line (title, then client · rule · age and its
 * badges) with its buttons on the right; opening it shows the rule's text,
 * the lever in full and the note.
 */
function DecisionRow({
  decision: d,
  clientName,
  showClient,
  focused = false,
  writes,
  sessionsOn,
  resolving,
  onResolve,
}: {
  decision: OpsDecision;
  clientName: string;
  showClient: boolean;
  /** The row a link pointed at (`?decision=<id>`, the Slack push): opened and scrolled into view once. */
  focused?: boolean;
  writes: boolean;
  sessionsOn: boolean;
  resolving: string | null;
  onResolve: ResolveFn;
}) {
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(focused);
  const rowRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focused) rowRef.current?.scrollIntoView({ block: "center" });
  }, [focused]);
  const busy = resolving === d._id;
  const act = async (resolution: Resolution, opts?: { snoozeDays?: number }) => {
    setError(await onResolve(d, resolution, opts));
  };
  const manual = d.action?.type === "manual";
  // A judgment session only reads and files proposals, so it may start while writes are off.
  const runs = writes || (d.action?.type === "judgment_session" && sessionsOn);
  const observed = d.mode === "observe";
  const wouldDo = d.status === "accepted";
  const actionable = d.status === "open" || d.status === "snoozed";
  const title = titleWithoutClient(d.title, clientName);
  const detail = d.text && d.text.trim() !== d.title.trim() ? d.text : null;
  const lever = actionLabel(d.action);
  return (
    <li ref={rowRef} className={cn(wouldDo && "bg-muted/30", focused && "bg-primary/5")}>
      <div className="flex flex-col gap-2 px-4 py-2.5 @2xl:flex-row @2xl:items-center @2xl:gap-4">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="group flex min-w-0 flex-1 cursor-pointer items-start gap-3 text-left outline-none">
          <SeverityIcon level={d.severity} className="mt-0.5" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className={cn("text-sm font-medium group-hover:underline group-focus-visible:underline", !open && "line-clamp-1")}>{title}</span>
            <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
              {showClient && (
                <>
                  <span className="font-medium text-foreground/80">{clientName}</span>
                  <span aria-hidden>·</span>
                </>
              )}
              <span>{RULE_LABEL[d.rule]}</span>
              <span aria-hidden>·</span>
              <span title={`First raised ${new Date(d.created_at).toLocaleString()}`}>
                <RelativeTime at={d.created_at} />
                {d.gen > 1 && <span title="Raised again after being resolved"> · ×{d.gen}</span>}
              </span>
              {manual && (
                <Badge variant="muted" size="sm" title="A human lever: Done means you did it">
                  by hand
                </Badge>
              )}
              {d.mode === "auto" && (
                <Badge variant="info" size="sm" title="This rule acts by itself once writes are on">
                  auto
                </Badge>
              )}
              {observed && (
                <Badge variant="muted" size="sm" title="Observe mode: logged, no button (promote the rule in the plan)">
                  observed
                </Badge>
              )}
              {wouldDo && (
                <Badge variant="warning" size="sm" title="Accepted while writes were off: recorded as would-do, nothing was called">
                  would do
                </Badge>
              )}
              {d.status === "snoozed" && d.snoozed_until && (
                <Badge variant="muted" size="sm">
                  snoozed until {new Date(d.snoozed_until).toLocaleDateString()}
                </Badge>
              )}
            </span>
          </span>
          <ChevronDownIcon className={cn("mt-0.5 size-4 shrink-0 text-muted-foreground/60 transition-transform", open && "rotate-180")} />
        </button>
        {actionable && (
          <div className="flex shrink-0 items-center gap-1 pl-7 @2xl:pl-0">
            {!observed && (
              <Button
                size="xs"
                variant="secondary"
                disabled={busy}
                onClick={() => void act("accept")}
                title={manual ? "Mark it done by hand" : runs ? "Run the lever now" : "Record what would run (nothing is called while writes are off)"}
              >
                <CheckIcon />
                {manual ? "Done" : runs ? "Run" : "Accept"}
              </Button>
            )}
            <IconAction label="Snooze 3 days" busy={busy} onClick={() => void act("snooze", { snoozeDays: 3 })}>
              <ClockIcon />
            </IconAction>
            <IconAction label="Dismiss (returns only if the evidence changes)" busy={busy} onClick={() => void act("dismiss")}>
              <XIcon />
            </IconAction>
          </div>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-1.5 pr-4 pb-3 pl-11 text-sm">
          {detail && <p className="whitespace-pre-wrap text-muted-foreground">{detail}</p>}
          <p className="text-xs">
            <span className="text-muted-foreground">{manual ? "Lever (by hand): " : runs ? "Accept runs: " : "Accept would run: "}</span>
            {lever}
          </p>
          {d.note && <p className="text-xs text-muted-foreground italic">{d.note}</p>}
        </div>
      )}
      {error && <p className="pr-4 pb-2 pl-11 text-xs text-destructive">{error}</p>}
    </li>
  );
}

function rank(d: OpsDecision): number {
  const sev = d.severity === "critical" ? 0 : d.severity === "warn" ? 1 : 2;
  return sev * 10 + (d.action && d.action.type !== "manual" ? 0 : 1);
}

/** Proposals shown before "Show all": the rest are usually more of the same stuck states. */
const FIRST_ROWS = 8;

/** A sub-list inside the Decisions frame: an optional heading, then the rows. */
function Rows({ heading, children }: { heading?: string; children: ReactNode }) {
  return (
    <FramePanel className="@container overflow-hidden p-0">
      {heading && <p className="border-b px-4 py-2 text-xs font-medium text-muted-foreground">{heading}</p>}
      <ul className="divide-y">{children}</ul>
    </FramePanel>
  );
}

export function DecisionsFrame({
  decisions,
  clientNames,
  writes,
  sessionsOn,
  resolving,
  lastAction,
  onResolve,
  focusId = null,
}: {
  decisions: OpsDecision[];
  clientNames: Map<string, string>;
  writes: boolean;
  sessionsOn: boolean;
  resolving: string | null;
  lastAction: OpsActionRecord | null;
  onResolve: ResolveFn;
  /** A decision to open (`?decision=<id>` from the Slack push). */
  focusId?: string | null;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const [client, setClient] = useState<string>("all");
  const [showAll, setShowAll] = useState(false);
  const [focusApplied, setFocusApplied] = useState(false);
  const live = decisions.filter((d) => d.status === "open" || d.status === "snoozed" || d.status === "accepted");
  const proposals = live.filter((d) => d.mode !== "observe" && d.status !== "accepted").sort((a, b) => rank(a) - rank(b));
  const observed = live.filter((d) => d.mode === "observe" && d.status !== "accepted");
  const wouldDo = live.filter((d) => d.status === "accepted");
  const history = decisions.filter((d) => !live.includes(d));
  const name = (slug: string) => clientNames.get(slug) ?? slug;

  // The client filter: one segment per client with something to decide, busiest first.
  const perClient = [...proposals.reduce((m, d) => m.set(d.slug, (m.get(d.slug) ?? 0) + 1), new Map<string, number>())].sort((a, b) => b[1] - a[1]);
  const picked = client !== "all" && perClient.some(([slug]) => slug === client) ? client : "all";
  const filtered = picked === "all" ? proposals : proposals.filter((d) => d.slug === picked);
  const shown = showAll ? filtered : filtered.slice(0, FIRST_ROWS);
  const row = (d: OpsDecision) => (
    <DecisionRow key={d._id} decision={d} clientName={name(d.slug)} showClient={picked === "all"} focused={d._id === focusId} writes={writes} sessionsOn={sessionsOn} resolving={resolving} onResolve={onResolve} />
  );

  // A linked decision: switch to its client and make sure its row is rendered — once, then the filter is the reader's again.
  const focus = focusId ? proposals.find((d) => d._id === focusId) : undefined;
  useEffect(() => {
    if (focusApplied || !focus) return;
    setClient(focus.slug);
    setShowAll(proposals.filter((d) => d.slug === focus.slug).indexOf(focus) >= FIRST_ROWS);
    setFocusApplied(true);
  }, [focus, focusApplied, proposals]);
  const critical = proposals.some((d) => d.severity === "critical");

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle className="flex items-center gap-2">
          Decisions
          <Badge variant={proposals.length ? (critical ? "error" : "warning") : "ghost"}>{proposals.length} open</Badge>
          {wouldDo.length > 0 && <Badge variant="warning">{wouldDo.length} would do</Badge>}
        </FrameTitle>
        <FrameDescription>
          What the rules found, each with its lever; open a row for the details. {writes ? "Writes are on: Accept runs the lever." : "Writes are off: Accept only records what would run."}
        </FrameDescription>
        <FrameAction>
          <Button variant="ghost" size="sm" onClick={() => setShowHistory((h) => !h)} aria-expanded={showHistory}>
            <ChevronDownIcon className={cn("transition-transform", showHistory && "rotate-180")} />
            History
            <Badge variant="count" size="sm">
              {history.length}
            </Badge>
          </Button>
        </FrameAction>
      </FrameHeader>
      {lastAction && (
        <FramePanel className="py-2 text-sm">
          <span className="font-medium">Last click:</span> {lastAction.outcome.replace(/_/g, " ")} — {lastAction.detail}
          {lastAction.error && <span className="text-destructive"> ({lastAction.error})</span>}
        </FramePanel>
      )}
      {proposals.length === 0 ? (
        <FramePanel className="text-sm text-muted-foreground">Nothing to decide right now.</FramePanel>
      ) : (
        <FramePanel className="@container overflow-hidden p-0">
          {perClient.length > 1 && (
            <Tabs
              value={picked}
              onValueChange={(v) => {
                setClient(v);
                setShowAll(false);
              }}
            >
              <TabsList variant="underline" className="w-full justify-start overflow-x-auto border-b px-3 [scrollbar-width:none]" aria-label="Filter decisions by client">
                <ScopeWrapper>
                  <TabsTrigger value="all">
                    All <span className="ml-1 text-muted-foreground">{proposals.length}</span>
                  </TabsTrigger>
                </ScopeWrapper>
                {perClient.map(([slug, count]) => (
                  <ScopeWrapper key={slug}>
                    <TabsTrigger value={slug} className="whitespace-nowrap">
                      {name(slug)} <span className="ml-1 text-muted-foreground">{count}</span>
                    </TabsTrigger>
                  </ScopeWrapper>
                ))}
              </TabsList>
            </Tabs>
          )}
          <ul className="divide-y">{shown.map(row)}</ul>
          {filtered.length > FIRST_ROWS && (
            <div className="border-t px-4 py-1.5">
              <Button variant="ghost" size="xs" onClick={() => setShowAll((a) => !a)} aria-expanded={showAll}>
                <ChevronDownIcon className={cn("transition-transform", showAll && "rotate-180")} />
                {showAll ? "Show fewer" : `Show all ${filtered.length}`}
              </Button>
            </div>
          )}
        </FramePanel>
      )}
      {wouldDo.length > 0 && <Rows heading="Accepted while writes were off (would do)">{wouldDo.map((d) => <DecisionRow key={d._id} decision={d} clientName={name(d.slug)} showClient writes={writes} sessionsOn={sessionsOn} resolving={resolving} onResolve={onResolve} />)}</Rows>}
      {observed.length > 0 && <Rows heading="Observed (rules in observe mode — information only)">{observed.map((d) => <DecisionRow key={d._id} decision={d} clientName={name(d.slug)} showClient writes={writes} sessionsOn={sessionsOn} resolving={resolving} onResolve={onResolve} />)}</Rows>}
      {showHistory && history.length > 0 && (
        <Table variant="card">
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead className="text-right">When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((d) => (
              <TableRow key={d._id} className="text-muted-foreground">
                <TableCell>{name(d.slug)}</TableCell>
                <TableCell className="max-w-md whitespace-normal">
                  <span className="text-foreground">{titleWithoutClient(d.title, name(d.slug))}</span>
                  {d.note && <span className="block text-xs italic">{d.note}</span>}
                </TableCell>
                <TableCell>
                  {d.status}
                  {d.resolved_by && d.resolved_by !== "auto" && <span className="block text-xs">by {d.resolved_by.replace(/^user_/, "").slice(0, 12)}</span>}
                </TableCell>
                <TableCell className="text-right text-xs">{d.resolved_at ? <RelativeTime at={d.resolved_at} /> : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Frame>
  );
}

const SOURCING_TONE: Record<OpsSourcingRequest["status"], "info" | "success" | "error" | "muted" | "ghost"> = {
  proposed: "muted",
  approved: "info",
  queued: "info",
  running: "info",
  analyzed: "info",
  searched: "success",
  failed: "error",
  stalled: "error",
  dismissed: "ghost",
};

export function SourcingFrame({ requests, clientNames }: { requests: OpsSourcingRequest[]; clientNames: Map<string, string> }) {
  if (requests.length === 0) return null;
  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>
          Sourcing requests
          <FrameCount>{requests.length}</FrameCount>
        </FrameTitle>
        <FrameDescription>Batches the controller prepared from the plans' recipes. proposed → queued → running → analyzed → searched; the counts come from the staging pipeline. Detail lives on Batch status.</FrameDescription>
      </FrameHeader>
      <Table variant="card">
        <TableHeader>
          <TableRow>
            <TableHead>Batch</TableHead>
            <TableHead>Campaign</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right" title="Need: roster creators → records; bound = at most this many records fetched">
              Need → bound
            </TableHead>
            <TableHead>Pipeline</TableHead>
            <TableHead className="text-right">Roster</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((r) => (
            <TableRow key={r._id}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-mono text-xs">{r.batch_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {clientNames.get(r.slug) ?? r.slug} · {r.provider} {r.platform}
                  </span>
                </div>
              </TableCell>
              <TableCell className="max-w-56 truncate">{r.campaign_name}</TableCell>
              <TableCell>
                <Badge variant={SOURCING_TONE[r.status]} title={r.tracking_error ? `tracking: ${r.tracking_error}` : (r.error ?? undefined)}>
                  {r.status}
                </Badge>
                {r.tracking_error && <span className="block text-xs text-warning">tracking unavailable</span>}
              </TableCell>
              <TableCell className="text-right font-mono text-xs" title={r.need.basis}>
                {r.need.creators} → {r.need.records} / {r.upper_bound_records}
                {r.upper_bound_records > r.spend_ceiling_records && <span className="text-warning"> ↑ceiling</span>}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {r.counts ? `jobs ${r.counts.jobs.done}/${r.counts.jobs.total} · ${r.counts.raw.ACCEPTED} ok · ${r.counts.raw.PENDING} pending · ${r.counts.raw.REJECTED} rej` : "—"}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">{r.roster_before !== null ? `${r.roster_before} → ${r.roster_after ?? "…"}` : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Frame>
  );
}

const OUTCOME_TONE: Record<OpsActionRecord["outcome"], "warning" | "success" | "error" | "ghost"> = {
  would_do: "warning",
  executed: "success",
  failed: "error",
  blocked: "error",
  manual_done: "ghost",
};

export function ActionsFrame({ actions, clientNames }: { actions: OpsActionRecord[]; clientNames: Map<string, string> }) {
  const [open, setOpen] = useState(false);
  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>
          Actions log
          <FrameCount>{actions.length}</FrameCount>
        </FrameTitle>
        <FrameDescription>Every lever the controller ran or recorded: would-do (writes off), executed, failed, blocked, done by hand.</FrameDescription>
        <FrameAction>
          <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            <ChevronDownIcon className={cn("transition-transform", open && "rotate-180")} />
            {open ? "Hide" : "Show"}
          </Button>
        </FrameAction>
      </FrameHeader>
      {open &&
        (actions.length === 0 ? (
          <FramePanel className="text-sm text-muted-foreground">Nothing recorded yet.</FramePanel>
        ) : (
          <Table variant="card">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Detail</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {actions.map((a) => (
                <TableRow key={a._id}>
                  <TableCell className="text-xs">
                    <RelativeTime at={a.at} />
                  </TableCell>
                  <TableCell>{clientNames.get(a.slug) ?? a.slug}</TableCell>
                  <TableCell>
                    <Badge variant={OUTCOME_TONE[a.outcome]}>{a.outcome.replace(/_/g, " ")}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xl whitespace-normal">
                    {a.detail}
                    {a.error && <span className="block text-xs text-destructive">{a.error}</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.by === "auto" ? "auto rule" : a.by.replace(/^user_/, "").slice(0, 12)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ))}
    </Frame>
  );
}

const MODE_TONE = { auto: "success", propose: "info", observe: "muted" } as const;

export function CapabilitiesFrame({ capabilities: cap, clientNames }: { capabilities: OpsCapabilities; clientNames: Map<string, string> }) {
  const [open, setOpen] = useState(false);
  const slugs = [...clientNames.keys()];
  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>What the controller can do</FrameTitle>
        <FrameDescription>
          Rules in code, each with a mode per client (observe → propose → auto, set in the plan). Writes {cap.writes_enabled ? "ON" : "off"} · sourcing dispatch {cap.sourcing_dispatch ? "reachable" : "not configured here"} · Notion{" "}
          {cap.notion_reads ? "readable" : "no token"} · Slack push{" "}
          {cap.slack_push ? (cap.slack_push.on ? `on (digest ${String(cap.slack_push.digest_hour).padStart(2, "0")}:00 ${cap.slack_push.time_zone})` : "off (no webhook)") : "unknown"} · judgment sessions ≤{" "}
          {cap.judgment.per_client_daily_cap}/client/day, {cap.judgment.daily_cap}/day ({cap.judgment.model}).
        </FrameDescription>
        <FrameAction>
          <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            <ChevronDownIcon className={cn("transition-transform", open && "rotate-180")} />
            {open ? "Hide" : "Rules"}
          </Button>
        </FrameAction>
      </FrameHeader>
      {open && (
        <Table variant="card">
          <TableHeader>
            <TableRow>
              <TableHead>Rule</TableHead>
              <TableHead>What it does</TableHead>
              {slugs.map((s) => (
                <TableHead key={s} className="text-center">
                  {clientNames.get(s)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {cap.rules.map((r) => (
              <TableRow key={r.name}>
                <TableCell className="font-medium">{RULE_LABEL[r.name]}</TableCell>
                <TableCell className="max-w-xl whitespace-normal text-xs text-muted-foreground">{r.description}</TableCell>
                {slugs.map((s) => (
                  <TableCell key={s} className="text-center">
                    <Badge variant={MODE_TONE[r.modes[s] ?? r.default_mode]}>{r.modes[s] ?? r.default_mode}</Badge>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Frame>
  );
}
